import clsx from 'clsx'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import NavigationButtons from 'apps/employer_renewals/components/NavigationButtons'
import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import { useAllowances } from 'apps/employer_renewals/providers/AllowancesProvider'
import { useEmployer } from 'apps/employer_renewals/providers/EmployerProvider'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { type ComplianceCalculations } from 'components/hooks/quotes/useComplianceCalculations'
import { type Allowances } from 'components/quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import AnimateHeightAndOpacity from 'components/reusable/AnimateHeightAndOpacity'
import { useBreakpoint } from 'slab'

import RenewalComplianceChecks from '../RenewalComplianceChecks'
import FlatAllowanceConfigurator from './FlatAllowanceConfigurator'

type Props = {
  isLoading: boolean
  complianceCalculations: ComplianceCalculations
}

export default function FlatAllowancesContent ({
  isLoading,
  complianceCalculations,
}: Props) {
  const { breakpointIsAtLeast } = useBreakpoint()
  const { shouldShowQMEPage, shouldShowDependentCoveragePage, navigateTo } = useAppNavigation()
  const { saveAllowances, isSavingAllowances } = useAllowances()
  const { employerIsALE } = useEmployer()

  const [isEditing, setIsEditing] = useState(false)

  const methods = useForm<Allowances>({
    mode: 'onChange',
    defaultValues: {
      allowances: [],
    },
  })

  useEffect(() => {
    // The compliance calculations take a moment to settle, so we wait a bit before showing the editing mode
    const timeout = setTimeout(() => {
      if (!complianceCalculations.allChecksPassed && !isLoading) {
        setIsEditing(true)
      }
    }, 200)

    return () => clearTimeout(timeout)
  }, [complianceCalculations.allChecksPassed, isLoading])

  const onClickNext = async () => {
    const isSaved = await saveAllowances()
    if (!isSaved) {
      toast.error('Failed to save allowances')
      return
    }

    if (shouldShowQMEPage) {
      navigateTo(EmployerRenewalsRoute.ALLOWANCES_QME)
    } else if (shouldShowDependentCoveragePage) {
      navigateTo(EmployerRenewalsRoute.ALLOWANCES_DEPENDENTS_COVERAGE)
    } else {
      navigateTo(EmployerRenewalsRoute.ALLOWANCES_CONFIRM)
    }
  }

  const onSubmitInvalid = () => {
    setIsEditing(true)
  }

  return (
    <div className='flex flex-col'>
      <AnimateHeightAndOpacity isOpen={isEditing}>
        <div className='p-6'>
          <FormProvider {...methods}>
            <FlatAllowanceConfigurator />
          </FormProvider>
        </div>
      </AnimateHeightAndOpacity>
      <div className={clsx('flex p-6', {
        'justify-end': breakpointIsAtLeast('xs'),
        '[&>*]:flex-1': !breakpointIsAtLeast('xs'),
      })}
      >
        <NavigationButtons
          nextButtonText='Sounds good'
          isLoading={isSavingAllowances}
          onNext={employerIsALE
            ? methods.handleSubmit(onClickNext, onSubmitInvalid)
            : onClickNext}
          tertiaryButtonText={isEditing ? undefined : 'Review allowance'}
          onClickTertiary={() => setIsEditing(!isEditing)}
        />
      </div>
      <AnimateHeightAndOpacity isOpen={isEditing}>
        <div className='p-6 border-t border-gray-200'>
          <RenewalComplianceChecks complianceCalculations={complianceCalculations} isLoading={isLoading} />
        </div>
      </AnimateHeightAndOpacity>
    </div>
  )
}
